
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import Cookies from 'js-cookie';
import { useEffect, useState } from "react";

interface NavLink {
  name: string;
  href: string;
  dropdown?: { name: string; href: string; }[];
}

interface DesktopNavigationProps {
  navLinks: NavLink[];
  activeDropdown: string | null;
  setActiveDropdown: (name: string | null) => void;
  showUserDropdown: boolean;
  setShowUserDropdown: (show: boolean) => void;
}

const DesktopNavigation = ({
  navLinks,
  activeDropdown,
  setActiveDropdown,
  showUserDropdown,
  setShowUserDropdown,
}: DesktopNavigationProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check authentication status
  useEffect(() => {
    const checkAuth = () => {
      const token = Cookies.get('auth_token');
      const isDemo = localStorage.getItem('isDemo') === 'true';
      setIsAuthenticated(!!(token && !isDemo));
    };

    checkAuth();
    // Re-check on focus to handle login in another tab
    const handleFocus = () => checkAuth();
    window.addEventListener('focus', handleFocus);

    return () => window.removeEventListener('focus', handleFocus);
  }, [location]); // Re-check on route change

  const dropdownVariants = {
    hidden: { opacity: 0, y: -5, transition: { duration: 0.2 } },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3 } }
  };

  const toggleDropdown = (name: string) => {
    if (activeDropdown === name) {
      setActiveDropdown(null);
    } else {
      setActiveDropdown(name);
    }
  };

  return (
    <nav className="hidden md:flex items-center space-x-6">
      {navLinks.map((link, index) => (
        <div key={index} className="relative group">
          {link.dropdown ? (
            <button 
              onClick={() => toggleDropdown(link.name)}
              onMouseEnter={() => setActiveDropdown(link.name)}
              onMouseLeave={() => setActiveDropdown(null)}
              className={`transition-all text-sm font-medium hover:text-dineai-yellow flex items-center ${
                activeDropdown === link.name ? "text-dineai-yellow" : "text-[#43acff]"
              }`}
            >
              {link.name}
              <ChevronDown className={`ml-1 w-4 h-4 transition-transform ${activeDropdown === link.name ? 'rotate-180' : ''}`} />
            </button>
          ) : link.href.startsWith('#') ? (
            <a
              href={link.href}
              onClick={(e) => {
                e.preventDefault();
                const element = document.querySelector(link.href);
                element?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="transition-all text-sm font-medium hover:text-dineai-yellow text-[#43acff] cursor-pointer"
            >
              {link.name}
            </a>
          ) : (
            <Link
              to={link.href}
              className={`transition-all text-sm font-medium hover:text-dineai-yellow ${
                location.pathname === link.href ? "text-dineai-yellow" : "text-[#43acff]"
              }`}
            >
              {link.name}
            </Link>
          )}
          
          {link.dropdown && (
            <AnimatePresence>
              {activeDropdown === link.name && (
                <motion.div 
                  className="absolute left-0 mt-2 w-56 bg-[#2256e5] rounded-lg shadow-2xl overflow-hidden z-50 border border-white/20"
                  variants={dropdownVariants}
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                  onMouseEnter={() => setActiveDropdown(link.name)}
                  onMouseLeave={() => setActiveDropdown(null)}
                  style={{
                    boxShadow: '0 10px 30px -5px rgba(0, 0, 0, 0.3)',
                  }}
                >
                  <div className="py-1">
                    {link.dropdown.map((item, idx) => (
                      item.href.startsWith('#') ? (
                        <a
                          key={idx}
                          href={item.href}
                          onClick={(e) => {
                            e.preventDefault();
                            const element = document.querySelector(item.href);
                            element?.scrollIntoView({ behavior: 'smooth' });
                            setActiveDropdown(null);
                          }}
                          className="block px-4 py-3 text-sm text-white hover:bg-white/10 hover:text-dineai-yellow cursor-pointer transition-all"
                        >
                          {item.name}
                        </a>
                      ) : (
                        <Link
                          key={idx}
                          to={item.href}
                          className="block px-4 py-3 text-sm text-white hover:bg-white/10 hover:text-dineai-yellow transition-all"
                        >
                          {item.name}
                        </Link>
                      )
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          )}
        </div>
      ))}
      <div className="flex items-center space-x-4 ml-8">
        {isAuthenticated ? (
          <Link
            to="/restaurant-dashboard"
            className="text-sm font-medium text-[#43acff] hover:text-dineai-yellow transition-colors"
          >
            Dashboard
          </Link>
        ) : (
          <Link
            to="/restaurant-login"
            className="text-sm font-medium text-[#43acff] hover:text-dineai-yellow transition-colors"
          >
            Login
          </Link>
        )}

        <Link to="/restaurant-menu-search">
          <Button 
            className="bg-transparent border-2 border-dineai-yellow text-dineai-yellow hover:bg-dineai-yellow hover:text-black font-semibold px-6 py-2 text-sm rounded-full transform hover:scale-105 transition-all"
          >
            SIGN UP NOW
          </Button>
        </Link>
      </div>
    </nav>
  );
};

export default DesktopNavigation;
